import React, {useState, useMemo, useCallback, useEffect} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {Layout} from "components/UI/layout";
import {Preloader} from "components/Preloader";
import EmptyData from "components/EmptyData";
import {Tabs} from "components/Tabs";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import MultipleSelect from "components/selects/MultipleSelect";
import MiniSelect from "components/selects/MiniSelect";
import {availableInArray, requestsToUsSortOptions} from "constants/select.consts";
import RequestsToUsHead from "./components/RequestsToUsHead";
import RequestsTableHead from "./components/RequestsTableHead";
import RequestsTableItem from "./components/RequestsTableItem";
import {PaginationType} from "components/Pagination";
import {requestsToUsTabs} from "constants/tabs.const";
import {useSavedFilters} from "helpers/userHooks";

const defaultFilters = {
  isDeadlinePassed: "not_passed",
  regions: null,
  sort: "RECEIVED_AT",
  activeTab: 0,
  page: 1,
};

const RequestsToUsPage = inject("store")(
  observer(({store: {seller}}) => {
    const {loading} = seller;
    const {t} = useTranslation();
    const [totalItems, setTotalItems] = useState(0);
    const [requests, setRequests] = useState([]);
    const [filteredRegions, setFilteredRegions] = useState(availableInArray);

    const [{own, all}, setRequestsCountAnalytic] = useState({
      own: 0,
      all: 0,
    });

    const sortOptions = useMemo(
      () =>
        requestsToUsSortOptions.map((option) => ({
          ...option,
          label: t(option.label),
        })),
      [t]
    );

    const updateFiltersCallback = useCallback(
      (values) => {
        const logic = requestsToUsTabs[values.activeTab]?.requestKey;
        const regions =
          Array.isArray(values?.regions) && values?.regions?.filter(Boolean)?.length
            ? values.regions.filter(Boolean)
            : null;

        seller
          .getRequests(filters?.page, logic, {
            isDeadlinePassed:
              values?.sort === "CLOSEST_TO_DEADLINE"
                ? String(values?.isDeadlinePassed)
                : null,
            regions,
            sort: values?.sort,
            page: values?.page,
          })
          .then((data) => {
            if (data) {
              setTotalItems(data.totalItems);
              setRequests(data.requests);
            }
          });
        return values;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const updateRequestsCountAnalytic = useCallback(
      (values) => {
        seller
          .getRequestsCountAnalytic({
            isDeadlinePassed:
              values?.sort === "CLOSEST_TO_DEADLINE"
                ? String(values?.isDeadlinePassed)
                : null,
            regions: values?.regions,
          })
          .then((nonReadCount) => {
            if (nonReadCount) {
              const {own: newOwn, all: newAll} = nonReadCount;
              if (
                (newOwn === 0 && own !== 0) ||
                (newAll === 0 && all !== 0) ||
                (newOwn === own && newAll === all)
              ) {
                return;
              }
              setRequestsCountAnalytic(nonReadCount);
            }
          });
      },
      [setRequestsCountAnalytic, seller, own, all]
    );

    const [filters, setFilters] = useSavedFilters({
      key: "requestsToUsFilters",
      defaultValue: defaultFilters,
      allowedValues: {
        activeTab: ["0", "1"],
        sort: [
          "RECEIVED_AT",
          "CLOSEST_TO_DEADLINE",
          "UNREAD_FIRST",
          "BUYER_NAME",
          "PROJECT_NAME",
        ],
        isDeadlinePassed: ["passed", "not_passed"],
      },
      storage: "local",
      updateFilters: (values) => {
        const requestParams = {
          ...values,
          isDeadlinePassed:
            values?.sort === "CLOSEST_TO_DEADLINE"
              ? String(values?.isDeadlinePassed)
              : null,
          regions:
            Array.isArray(values?.regions) && values?.regions?.filter(Boolean)?.length
              ? values.regions.filter(Boolean)
              : null,
          sort: values?.sort,
        };
        updateFiltersCallback(requestParams);
        updateRequestsCountAnalytic(requestParams);
      },
    });

    const updateFilters = useCallback(
      (updates) => {
        const newFilters = {...filters, ...updates};
        setFilters(newFilters);
      },
      [filters, setFilters]
    );

    // TODO: delete till we feel confident users dont have an old stored slug anymore
    // @teymur: Remove isRejected from this page and from the API instead of this ugly reset
    useEffect(() => {
      if (filters?.activeTab > 1 || filters?.isRejected === true) {
        updateFilters(defaultFilters);
      }
    }, [filters, updateFilters]);

    const handlePageChange = useCallback(
      (pageNumber) => {
        if (filters?.page !== pageNumber) {
          updateFilters({page: pageNumber});
        }
      },
      [updateFilters, filters?.page]
    );

    const handleRegionChange = useCallback(
      (value) => {
        updateFilters({regions: value.filter(Boolean), page: 1});
        updateRequestsCountAnalytic(filters);
      },
      [updateFilters, updateRequestsCountAnalytic, filters]
    );

    const filterAvailableInArray = useCallback(
      (newAvailableInArray = []) =>
        availableInArray.filter((region) =>
          newAvailableInArray.some((availableRegion) => availableRegion === region.value)
        ),
      []
    );

    useEffect(() => {
      const logic = requestsToUsTabs[filters?.activeTab]?.requestKey;
      seller
        .getAvailableRegions(logic || "own")
        .then((regions) => setFilteredRegions(filterAvailableInArray(regions)));
    }, [seller, filterAvailableInArray, filters?.activeTab]);

    const handleTabChange = useCallback(
      (value) => {
        if (filters?.activeTab !== value) {
          updateFilters({activeTab: value, page: 1});
          updateRequestsCountAnalytic(filters);
          const logic = requestsToUsTabs.find((tab) => tab.id === value)?.requestKey;
          seller.getAvailableRegions(logic).then((regions) => {
            setFilteredRegions(filterAvailableInArray(regions));
          });
        }
      },
      [
        updateFilters,
        updateRequestsCountAnalytic,
        filterAvailableInArray,
        seller,
        filters,
        setFilteredRegions,
      ]
    );

    const handleDeadlineChange = useCallback(
      (value) => {
        updateFilters({
          isDeadlinePassed: value ? "passed" : "not_passed",
          page: 1,
        });
        updateRequestsCountAnalytic(filters);
      },
      [updateFilters, updateRequestsCountAnalytic, filters]
    );

    const handleSortChange = useCallback(
      (value) => {
        if (value === "CLOSEST_TO_DEADLINE" && filters?.sort !== "CLOSEST_TO_DEADLINE") {
          updateFilters({
            sort: value,
            isDeadlinePassed: filters?.isDeadlinePassed || "not_passed",
            page: 1,
          });
        } else {
          updateFilters({sort: value, isDeadlinePassed: "not_passed"});
        }
      },
      [updateFilters, filters?.sort, filters?.isDeadlinePassed]
    );

    return (
      <Layout>
        <div className="requests-to-us">
          <RequestsToUsHead />
          {filters ? (
            <>
              <div className="requests-to-us-filters-wrapper">
                <div className="requests-to-us__filters df-row-center mb-4">
                  <Tabs
                    className="requests-to-us__tabs"
                    tabs={requestsToUsTabs}
                    active={parseInt(filters?.activeTab) || 0}
                    counts={[all, own]}
                    badgeView
                    setActive={handleTabChange}
                    addClass="ws-nowrap"
                  />
                  <div className="df-row-center">
                    <label className="custom-switch_label __uppercase __small __bold mr-1 ws-nowrap">
                      {t("REQUESTS_TO_US_SORT_LABEL")}
                    </label>
                    <MiniSelect
                      list={sortOptions}
                      defaultValue={filters?.sort || "UNREAD_FIRST"}
                      prefixLabel
                      itemAsObject
                      onChange={handleSortChange}
                    />
                    {filters?.sort === "CLOSEST_TO_DEADLINE" && (
                      <CustomRadioSwitch
                        label={t("REQUESTS_TO_US_FILTERS_BAR_PAST_DEADLINE")}
                        checked={filters?.isDeadlinePassed === "passed"}
                        uppercase
                        small
                        labelOffset={1}
                        addWrapperClass="ml-4"
                        isActive={handleDeadlineChange}
                      />
                    )}
                  </div>

                  <div className="df-row-jce-center fg1">
                    <MultipleSelect
                      addClass="mr-1"
                      list={filteredRegions}
                      leftLabel={t("FILTER_REQUEST_TO_US_BY_REGION_LABEL")}
                      uppercaseLabel
                      tinyLabel
                      boldLabel
                      value={
                        Array.isArray(filters?.regions) &&
                        filters?.regions?.filter(Boolean).length
                          ? filters.regions.filter(Boolean)
                          : null
                      }
                      onChange={handleRegionChange}
                      small
                      color="green"
                    />
                  </div>
                </div>
              </div>
              {loading && <Preloader addClass="preloader500" />}
              {!loading && (
                <div className="requests-to-us content-block_wrapper">
                  <RequestsTableHead />
                  {requests?.length ? (
                    requests?.map((item) => (
                      <RequestsTableItem key={item.id} request={item} />
                    ))
                  ) : (
                    <EmptyData addClass={"empty-data pt-4"} />
                  )}
                </div>
              )}
              <PaginationType
                totalItems={totalItems}
                currentPage={filters?.page}
                onPageChange={handlePageChange}
                addClass="mt-8"
              />
            </>
          ) : null}
        </div>
      </Layout>
    );
  })
);

export default RequestsToUsPage;
